import { ToggleButton, ToggleButtonGroup, Tooltip, Box } from "@mui/material";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import { useTranslation } from "react-i18next";
import { PosLayoutMode } from "./posLayouts";

interface Props {
	mode: PosLayoutMode;
	onChange: (mode: PosLayoutMode) => void;
	/** Denser control for the top bar (icons always; labels from sm up). */
	compact?: boolean;
}

const PosLayoutToggle = ({ mode, onChange, compact = false }: Props) => {
	const { t } = useTranslation();

	return (
		<Box
			sx={{
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				py: compact ? 0 : 0.75,
				px: compact ? 0 : 1,
				flexShrink: 0,
			}}
		>
			<ToggleButtonGroup
				exclusive
				size="small"
				value={mode}
				onChange={(_, next: PosLayoutMode | null) => {
					if (next) onChange(next);
				}}
				aria-label={t("common:pos.layout.toggleAria")}
				sx={{
					bgcolor: "background.paper",
					boxShadow: compact ? 0 : 1,
					borderRadius: 2,
					"& .MuiToggleButton-root": {
						px: compact ? { xs: 0.75, sm: 1.25 } : 1.5,
						py: compact ? 0.35 : undefined,
						textTransform: "none",
						gap: 0.75,
						border: compact ? undefined : undefined,
					},
				}}
			>
				<ToggleButton value="classic" aria-label={t("common:pos.layout.classic")}>
					<Tooltip title={t("common:pos.layout.classic")}>
						<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
							<ViewAgendaOutlinedIcon fontSize="small" />
							<Box
								component="span"
								sx={{ display: compact ? { xs: "none", md: "inline" } : "inline" }}
							>
								{t("common:pos.layout.classic")}
							</Box>
						</Box>
					</Tooltip>
				</ToggleButton>
				<ToggleButton value="visual" aria-label={t("common:pos.layout.visual")}>
					<Tooltip title={t("common:pos.layout.visual")}>
						<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
							<GridViewOutlinedIcon fontSize="small" />
							<Box
								component="span"
								sx={{ display: compact ? { xs: "none", md: "inline" } : "inline" }}
							>
								{t("common:pos.layout.visual")}
							</Box>
						</Box>
					</Tooltip>
				</ToggleButton>
				<ToggleButton value="split" aria-label={t("common:pos.layout.split")}>
					<Tooltip title={t("common:pos.layout.split")}>
						<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
							<ViewColumnOutlinedIcon fontSize="small" />
							<Box
								component="span"
								sx={{ display: compact ? { xs: "none", md: "inline" } : "inline" }}
							>
								{t("common:pos.layout.split")}
							</Box>
						</Box>
					</Tooltip>
				</ToggleButton>
			</ToggleButtonGroup>
		</Box>
	);
};

export default PosLayoutToggle;
